import { atom } from 'jotai';
import type { MessageDescriptor } from 'react-intl';

import { sessionRecordingMessages } from './sessionRecording.messages';

/**
 * Bridge between the ``ui.session_recording`` agent-event dispatcher (which
 * runs outside React, in the event coordinator) and the mounted
 * {@link AgentSessionRecordingControl} that actually drives the recorder.
 *
 * The dispatcher sets {@link agentRecordingCommandAtom} and awaits the promise
 * registered via {@link registerAgentRecordingCommand}; the controller picks up
 * the command, performs it, then calls {@link settleAgentRecordingCommand} so
 * the dispatcher can ack the agent (executed / failed).
 */

export type AgentRecordingAction = 'start' | 'pause' | 'resume' | 'stop';

export interface AgentRecordingCommand {
  /** Unique per emit; correlates the atom write with its settle. */
  id: string;
  action: AgentRecordingAction;
  /** Explicit target; when null the controller resolves the open/active session. */
  sessionId: string | null;
}

export const agentRecordingCommandAtom = atom<AgentRecordingCommand | null>(null);

/**
 * Submit a plain user turn to the active project chat, set by ProjectChat while
 * mounted. Lets surfaces outside the composer (the session-artifact recording
 * controls) route an action THROUGH the agent — the agent then runs
 * ui.session_recording, so the action both drives the recorder and shows as a
 * row in the transcript. ``null`` when no project chat is mounted (callers then
 * fall back to driving the recorder directly).
 */
export const projectChatSubmitAtom = atom<((text: string) => void) | null>(null);

/** Human-facing turn text per action, so a manual control reads naturally in
 * the transcript ("Pause transcribing") and the agent maps it to the tool.
 * Descriptors, not strings: callers format with their `intl` so the turn text
 * follows the user's locale. Reuses the control-label messages so the button a
 * user clicks and the turn it submits stay one translation. */
export const AGENT_RECORDING_REQUEST_TEXT: Record<
  AgentRecordingAction,
  MessageDescriptor
> = {
  start: sessionRecordingMessages.startTranscribing,
  resume: sessionRecordingMessages.resumeTranscribing,
  pause: sessionRecordingMessages.pauseTranscribing,
  stop: sessionRecordingMessages.stopTranscribing,
};

interface PendingResolver {
  resolve: () => void;
  reject: (reason: Error) => void;
}

const pending = new Map<string, PendingResolver>();

/** Register a promise the dispatcher awaits until the controller settles ``id``. */
export const registerAgentRecordingCommand = (id: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    pending.set(id, { resolve, reject });
  });

/**
 * Drop a pending entry WITHOUT settling its promise. The dispatcher calls this
 * after its own timeout has already rejected the awaited promise, so the
 * never-settled resolver can't leak one Map entry per timed-out command.
 * No-op if already settled.
 */
export const discardAgentRecordingCommand = (id: string): void => {
  pending.delete(id);
};

/**
 * Settle a pending command. ``ok=false`` rejects so the dispatcher throws and
 * the agent gets a failed ack (e.g. ``no_active_session``). No-op if the id was
 * already settled (e.g. timed out) or never registered.
 */
export const settleAgentRecordingCommand = (
  id: string,
  ok: boolean,
  reason?: string
): void => {
  const resolver = pending.get(id);
  if (!resolver) return;
  pending.delete(id);
  if (ok) {
    resolver.resolve();
  } else {
    resolver.reject(new Error(reason ?? 'agent recording command failed'));
  }
};
